import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMerchant } from "../../../context/StateContext/StateProvider";
import useInstallments from "hooks/useInstallments";
import { installmentsFormInfo, setDefaultBlockedIssuersValues, setDefaultInstallmentsValues } from "./utils";
import { getFinancingOptions } from "utils/utilities";
import LayoutV2 from "layouts/V2";
import BaseButton from "../../../baseComponents/BaseButton";
import BaseSelect from "../../../baseComponents/BaseSelect";
import BaseCheckbox from "baseComponents/BaseCheckbox";
import BaseLoading from "baseComponents/BaseLoading";
import { BlockedIssuersProps, DefaultBlockedIssuersState, InstallmentsProps } from "./types";
import { logAmplitudeEvent } from "hooks/useAmplitude";
import { Metrics } from "metrics/events";

import styles from "../installments.module.scss";
import { InstallmentsTitle } from "./InstallmentsTitle";
import { BlockedIssuersTitle } from "./BlockedIssuersTitle";

const INSTALLMENTS_OPTIONS = [3,6,12,18,24];
const ISSUERS_OPTIONS = [
  {name: "american_express", label: "American Express"},
  {name: "cabal", label: "Cabal"},
  {name: "maestro", label: "Maestro"},
  {name: "naranja", label: "Naranja"},
  {name: "diners_club", label: "Diners Club"},
  {name: "confiable", label: "Confiable"},
];

const InstallmentsForm = () => {
  const navigate = useNavigate();
  const { jwe } = useParams();
  const { state, sendInstallmentsData, getMerchantDataFromJwe } = useMerchant();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  const [defaultInstallmentsState, setDefaultInstallmentsState] = useState<object>({
    3: undefined,
    6: undefined,
    12: undefined,
    18: undefined,
    24: undefined,
  });

  const [defaultBlockedIssuersState, setDefaultBlockedIssuersState] = useState<DefaultBlockedIssuersState>({
    american_express: false,
    cabal: false,
    maestro: false,
    naranja: false,
    diners_club: false,
    confiable: false,
  });


  useEffect(() => {
    handleGetData();
  }, []);

  const handleBlockedIssuersCheckboxChange = (value: boolean, field: string) => {
    setDefaultBlockedIssuersState({
      ...defaultBlockedIssuersState,
      [field]: value
    });
  };

  const handleGetData = async () => {
    setIsLoading(true);
    try {
      const response = await getMerchantDataFromJwe(jwe);
      const {installments, issuers } = response.response.data;
      
      if (response.success) {
        setDefaultInstallmentsState(setDefaultInstallmentsValues(installments.installmentsPlans));
        setDefaultBlockedIssuersValues(issuers, defaultBlockedIssuersState);
      } 
    } catch (error) {
      navigate("/installments-expired");
    }
    setIsLoading(false);
  };

  const handleSendData = (installmentsBody: InstallmentsProps, blockedIssuersBody: BlockedIssuersProps) => {
    logAmplitudeEvent(Metrics.CONFIRM_INSTALLMENTS_BUTTON_TAPPED);
    sendInstallmentsData(jwe, installmentsBody, blockedIssuersBody);
  };

  const { errors, handleSubmit } = useInstallments(handleSendData);

  return (
    <LayoutV2>
      <div className={styles.body}>
        <div className={`${styles.body__title} title`}>
          {installmentsFormInfo.title}
        </div>
        <div className={styles.body__example}>
          <div className={`sub-title`}>
            {installmentsFormInfo.subtitle}
          </div>
        </div>
        <form className={styles.body__form} onSubmit={(e) => handleSubmit(e, defaultBlockedIssuersState)}>
          <InstallmentsTitle/>
          {
            isLoading ?
              <BaseLoading isLoading={true} /> :
              <>
                {
                  INSTALLMENTS_OPTIONS.map( (numberOfInstallments: number, index: number) => {
                    const financingOptions = index < 2 ? getFinancingOptions(numberOfInstallments) : getFinancingOptions(numberOfInstallments).slice(0, 3);
                    return (
                      <BaseSelect
                        key={numberOfInstallments}
                        options={financingOptions}
                        name={`${numberOfInstallments}`}
                        label={`${numberOfInstallments} cuotas`}
                        placeholder={`Ej: ${numberOfInstallments} cuotas SIN interés`}
                        info="Seleccioná la cantidad de cuotas"
                        error={errors[numberOfInstallments]}
                        defaultValue={defaultInstallmentsState[numberOfInstallments]}
                        maxMenuHeight={200}
                      />
                    );
                  })
                }
              </>
          }
          <section className={styles.checkbox__group}>
            <BlockedIssuersTitle />
            {
              isLoading ?
                <BaseLoading isLoading={true} /> :
                <>
                  {
                    ISSUERS_OPTIONS.map( issuer => 
                        <BaseCheckbox
                          key={issuer.name}
                          name={issuer.name}
                          label={issuer.label}
                          isChecked={defaultBlockedIssuersState[issuer.name]}
                          onChange={(value: boolean) => handleBlockedIssuersCheckboxChange(value, issuer.name)}
                        />
                    )
                  } 
                </>
            }
          </section>
          <div className={styles.body__button}>
            <BaseButton title="Confirmar" isLoading={state.isLoading} typeButton="submit" />
          </div>
        </form>
      </div>
    </LayoutV2>
  );
};

export default InstallmentsForm;